import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, Loader2, SkipForward, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { useGatewayStore } from "@/stores/useGatewayStore";
import type { ScheduleJobView, ScheduleRunRow } from "@/hooks/useScheduleJobs";

/**
 * `ScheduleRunsDrawer` — one job's run history (agendamentos plan P3; R2
 * handoff §3.3): `schedule.runs {jobId}`, newest first, **honest failure
 * rendering** — an errored run shows its error, never a soft "concluído".
 *
 * Retention is 30 days (operator-pinned), so a job that has not run recently
 * legitimately returns `[]` → "sem execuções recentes", NOT an error state.
 * That distinction is the whole point: an empty history and a broken history
 * must never look the same.
 */
export function ScheduleRunsDrawer({
  job,
  onClose,
}: {
  job: ScheduleJobView;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const rpc = useGatewayStore((s) => s.rpc);
  const [runs, setRuns] = useState<ScheduleRunRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopImmediatePropagation();
      onClose();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);

  useEffect(() => {
    if (!rpc) return;
    let cancelled = false;
    rpc
      .call<{ runs?: ScheduleRunRow[] }>("schedule.runs", { jobId: job.id, limit: 50 })
      .then((res) => {
        if (!cancelled) setRuns(res?.runs ?? []);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The runtime's message is user-facing PT-BR — verbatim.
        setError(err instanceof Error ? err.message : String(err));
        setRuns([]);
      });
    return () => {
      cancelled = true;
    };
  }, [rpc, job.id]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="schedule-runs-drawer"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl dark:bg-coffee-900"
      >
        <div className="mb-3 flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-serif-display text-lg text-coffee-900 dark:text-cream-100">
              {t("schedule.runs.title")}
            </h2>
            <p className="truncate text-body-sm text-coffee-600 dark:text-cream-400">
              {job.title} · {job.scheduleHuman}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close")}
            title={t("common.close")}
            data-testid="schedule-runs-close"
            className="shrink-0 rounded-lg p-1.5 text-coffee-500 transition-colors hover:bg-cream-100 hover:text-coffee-800 dark:text-cream-500 dark:hover:bg-coffee-800"
          >
            <X size={16} />
          </button>
        </div>

        {runs === null ? (
          <p className="flex items-center gap-2 py-6 text-body-sm text-coffee-500 dark:text-cream-500">
            <Loader2 size={14} className="animate-spin" aria-hidden />
            {t("common.loading", "Carregando…")}
          </p>
        ) : error ? (
          <p
            className="rounded bg-red-500/10 p-2 text-xs text-red-600 dark:text-red-300"
            data-testid="schedule-runs-error"
          >
            {error}
          </p>
        ) : runs.length === 0 ? (
          <p
            className="py-6 text-center text-body-sm text-coffee-500 dark:text-cream-500"
            data-testid="schedule-runs-empty"
          >
            {t("schedule.noRecentRuns")}
            <span className="mt-1 block text-[11px]">{t("schedule.runs.retentionNote")}</span>
          </p>
        ) : (
          <ul className="space-y-1.5" data-testid="schedule-runs-list">
            {runs.map((run, i) => (
              <li
                key={`${run.ts}-${i}`}
                data-testid="schedule-run-row"
                data-status={run.status}
                className="rounded-lg border border-cream-200/70 px-3 py-2 dark:border-coffee-800"
              >
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={cn(
                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
                      run.status === "ok"
                        ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
                        : run.status === "error"
                          ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
                          : "bg-cream-200 text-coffee-600 dark:bg-coffee-800 dark:text-cream-400",
                    )}
                  >
                    {run.status === "ok" ? (
                      <Check size={11} />
                    ) : run.status === "error" ? (
                      <AlertTriangle size={11} />
                    ) : (
                      <SkipForward size={11} />
                    )}
                  </span>
                  <span className="text-body-sm text-coffee-800 dark:text-cream-200">
                    {t(`schedule.runStatus.${run.status}`, run.status)}
                  </span>
                  <span className="ml-auto text-[11px] text-coffee-500 dark:text-cream-500">
                    {new Date(run.ts).toLocaleString()}
                    {typeof run.durationMs === "number" && ` · ${Math.round(run.durationMs / 1000)}s`}
                  </span>
                </div>
                {run.summary && (
                  <p className="mt-1 text-[11px] text-coffee-600 dark:text-cream-400">
                    {run.summary}
                  </p>
                )}
                {/* Honest failure rendering — the error is shown, not hidden
                    behind a generic status word. */}
                {run.error && (
                  <p className="mt-1 text-[11px] text-red-600 dark:text-red-300">{run.error}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
